import React, { useState, useRef, useEffect } from 'react'
import Modal from "@mui/material/Modal";
import closeIcon from "../../../../../Assets/close-icon.svg"
import { useDispatch, useSelector } from 'react-redux';
import { SET_MODAL } from '../../../../../redux/actionTypes/modalActionType';
import { MdContentCopy } from 'react-icons/md'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { serviceConsumer } from '../../../../../network/ServiceConsumer';

const modalCloseHandler = (dispatch) => {
    dispatch({
        type: SET_MODAL,
        payload: {
            modal: "",
            eventProp: "",
        }
    })
}

// The Direct Link carries the event's key: registrations through it are not
// stopped by the event's cap (backend helpers/directLinkKey.js).
const LOAD_FAIL = "Couldn't load the direct link. Please try again.";
const RESET_FAIL = "Couldn't reset the link. Please try again.";
const RESET_UNSURE = "The reset may not have finished. The link shown is the current one.";

const DirectUrlModal = ({ modalState, manageType }) => {
    const modal = useSelector((state) => state.modalReducer);
    const [copied, setCopied] = useState(false)
    const [linkKey, setLinkKey] = useState("")
    const [loadError, setLoadError] = useState("")
    const [confirmReset, setConfirmReset] = useState(false)
    const [busy, setBusy] = useState(false)
    // only the latest request may update the modal (event switch, retry, reset)
    const requestSeq = useRef(0);
    const inputRef = useRef(null);
    const dispatch = useDispatch();

    const isFeis = manageType === "feis";
    const keyUrl = `${process.env.REACT_APP_BASE_URL}/${isFeis ? "feis" : "grade-exam"}/${modal.eventId}/direct-link`;
    const link = `${process.env.REACT_APP_BASE}/${isFeis ? "register-dancers" : "register-grade-exam"}?id=${modal.eventId}&type=redirect&key=${encodeURIComponent(linkKey)}`;

    // Clears the shown link, then shows the key the server returns.
    const fetchKey = async (method, url, failMessage) => {
        const seq = ++requestSeq.current;
        setBusy(true);
        setLinkKey("");
        setCopied(false);
        setLoadError("");
        let ok = false;
        try {
            const response = await serviceConsumer(method, url);
            if (seq !== requestSeq.current) return { current: false };
            ok = Boolean(response?.data?.key);
            if (ok) setLinkKey(response.data.key);
            else setLoadError(failMessage);
        } catch (error) {
            if (seq !== requestSeq.current) return { current: false };
            setLoadError(error?.messages || failMessage);
        }
        setBusy(false);
        return { current: true, ok };
    };

    const loadKey = () => fetchKey("GET", keyUrl, LOAD_FAIL);

    useEffect(() => {
        setConfirmReset(false);
        if (modalState && modal.eventId) loadKey();
        return () => { requestSeq.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalState, modal.eventId, manageType]);

    const handleCopy = () => {
        if (!linkKey || busy) return;
        const textToCopy = inputRef.current.value;
        navigator.clipboard.writeText(textToCopy)
            .then(() => { setCopied(true) })
            .catch((error) => { });
    };

    const handleReset = async () => {
        setConfirmReset(false);
        const reset = await fetchKey("POST", `${keyUrl}/reset`, RESET_FAIL);
        if (reset.current && !reset.ok) {
            // the reset may have gone through without an answer: show the key
            // the server has now rather than a possibly stale one
            const reload = await loadKey();
            if (reload.current && reload.ok) setLoadError(RESET_UNSURE);
        }
    };

    return (
        <Modal sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "10px" }} open={modalState}>
            <div className="org-modal-url d-flex gap-2" style={{ width: "auto", padding: "20px", }}>
                <div className="direct-link-body">
                    <div className="copyUrlDiv">
                        <input
                            className="urlInput"
                            type="text"
                            value={linkKey ? link : (busy ? "Preparing link..." : "")}
                            ref={inputRef}
                            disabled
                        />
                        <div onClick={handleCopy} style={{ cursor: linkKey && !busy ? "pointer" : "default" }} aria-label="Copy link" role="button">
                            <MdContentCopy style={{ color: copied ? "green" : "black" }} />
                        </div>
                    </div>
                    {copied && <div style={{ color: "green", fontSize: "14px" }}>Copied!</div>}
                    {loadError && (
                        <div className="direct-link-error">
                            {loadError}{" "}
                            {!linkKey && !busy && <button type="button" className="direct-link-text-btn" onClick={loadKey}>Try again</button>}
                        </div>
                    )}
                    <p className="direct-link-note">
                        Registrations through this link are not stopped by the event's cap (dancers who use it still count toward it). Reset the link to stop old copies from skipping the cap.
                    </p>
                    {linkKey && !busy && !confirmReset && (
                        <button type="button" className="direct-link-text-btn" onClick={() => setConfirmReset(true)}>
                            Reset link
                        </button>
                    )}
                    {confirmReset && (
                        <div className="direct-link-confirm">
                            <div>Reset the link? Old copies will still open registration, but the cap will apply to them.</div>
                            <div className="direct-link-confirm-actions">
                                <button type="button" className="direct-link-danger-btn" disabled={busy} onClick={handleReset}>
                                    Reset
                                </button>
                                <button type="button" className="direct-link-text-btn" disabled={busy} onClick={() => setConfirmReset(false)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <img
                        src={closeIcon}
                        alt="close-icon"
                        onClick={() => modalCloseHandler(dispatch)}
                        style={{ marginLeft: "10px", cursor: "pointer", marginTop: "15px" }}
                    />
                </div>
            </div>
        </Modal >
    )
}

export default DirectUrlModal

export const ScheduledPdf = ({ modalState, name, deleteFn }) => {
    const dispatch = useDispatch();
    return (
        <Modal sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "10px" }} open={modalState}>
            <div className="org-modal-div bg-white d-flex flex-row align-items-center gap-2 p-3 border border-dark">
                {/* <div className="fileIcon d-flex justify-content-center">{<AttachFileIcon />}</div> */}
                <div style={{ overflowX: "scroll", whiteSpace: "nowrap" }} className=" flex-grow-1 py-3 ps-3">{name}</div>
                <div className="pdfDlticon d-flex justify-content-center flex-end">
                    <DeleteOutlineIcon onClick={() => deleteFn("", "", true)} />
                </div>
                <img
                    className="pe-3"
                    src={closeIcon}
                    alt="close-icon"
                    onClick={() => modalCloseHandler(dispatch)}
                    style={{ cursor: "pointer" }}
                />
            </div>
        </Modal >
    )
}